// react
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

// router
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// context
import { ContextProject } from '../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2/ProjectV2.context';
import { ContextBanner } from '../../../App.context';

// hooks
import { useActivity } from '../../../hooks/usePlanningActivities';

// components
import { PageDetailSkeleton } from '../../ui/PageDetailSkeleton/PageDetailSkeleton';

// mui
import { alpha, Box, Chip, Grid, Stack, Typography } from '@mui/material';

// utils
import {
	getContractAmountForCurrency,
	getContractAmountPaidReporting,
	getContractAmountsByCurrency,
	getContractCommitmentAmount,
	getContractNum,
	getContractSupplierName,
} from '../ProjectV2/Contracts/Contracts.utils';

// icons
import {
	AccountTree as IconActivity,
	DeleteOutline as IconDelete,
	Description as IconContract,
	Assignment as IconWorkPlan,
} from '@mui/icons-material';

// utils
import { formatCurrency } from '../../../utils/currency';
import { getProjectLocalCurrency } from '../../../utils/localCurrency';
import { resolveActivityFxRate } from '../../../utils/fxRate';

// cards
import { CardActivities } from '../../cards/CardActivities/CardActivities';
import { CardPhysicalProgress } from '../../cards/CardPhysicalProgress/CardPhysicalProgress';

// dialogs
import { DialogActions } from '../../dialogs/DialogActions/DialogActions';

// v2 components
import { CardAiDocuments } from '../../cards/CardAiDocuments/CardAiDocuments';
import { CardDetail } from '../../cards/CardDetail/CardDetail';
import {
	getStatusColor,
	getStatusLabel,
	HeaderDetailPage,
} from '../../ui/HeaderDetailPage/HeaderDetailPage';
import { TruncatedText } from '../../ui/TruncatedText/TruncatedText';
import { customFieldsToStats } from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';

// data
import { GRADIENTS } from '../../../data';

// design system
import { spacing } from '../../../utils/designSystem';

export const PagePlanningActivity = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { activityId } = useParams<{ activityId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const activityIdNumber = Number(activityId);

	// Fetch activity by ID
	const {
		data: activity,
		loading: activityLoading,
		refetch,
	} = useActivity({
		id: activityIdNumber,
		isEnabled: Number.isFinite(activityIdNumber),
		query: { populate: 'workPlan,entity,parent,component,category,expenseType' },
	});

	// Track dialog state for refetch on close
	const dialogParam = searchParams.get('dialog');
	const prevDialogRef = useRef<string | null>(dialogParam);

	// Refetch data when dialog closes (dialog param goes from value to null)
	useEffect(() => {
		if (prevDialogRef.current && !dialogParam) {
			// Dialog was open and is now closed - refetch data
			refetch();
		}
		prevDialogRef.current = dialogParam;
	}, [dialogParam, refetch]);

	// context
	const { project } = useContext(ContextProject);
	const { activities, workPlans, getContractsByActivity, deactivateContract } =
		useContext(ContextSpendPlan);
	const { setBanner } = useContext(ContextBanner);

	// handlers
	const handleEdit = () => {
		if (!activity) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'editActivity');
		params.set('dialogActivity', String(activity.id));
		setSearchParams(params);
	};

	const handleLinkContract = () => {
		if (!activity) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'linkContract');
		params.set('dialogActivity', String(activity.id));
		setSearchParams(params);
	};

	const handleAddSubActivity = () => {
		if (!activity) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'addActivity');
		params.set('dialogParentActivity', String(activity.id));
		const workPlanId =
			typeof activity.workPlan === 'number' ? activity.workPlan : activity.workPlan?.id;
		if (workPlanId) {
			params.set('dialogWorkPlan', String(workPlanId));
		}
		setSearchParams(params);
	};

	const handleOpenSubActivity = (subActivityId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/activity/${subActivityId}`);
	};

	const handleOpenContractPage = (contractId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/contract/${contractId}`);
	};

	const handleDeleteContracts = useCallback(async (selected: { id: number }[]) => {
		if (selected.length === 0) return;
		setPendingDeleteContracts(selected);
		setShowDeleteDialog(true);
	}, []);

	// State for delete confirmation dialog
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [pendingDeleteContracts, setPendingDeleteContracts] = useState<{ id: number }[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);

	const handleCancelDelete = () => {
		setShowDeleteDialog(false);
		setPendingDeleteContracts([]);
	};

	const handleConfirmDelete = useCallback(async () => {
		const selected = pendingDeleteContracts;
		const count = selected.length;
		setIsDeleting(true);

		let successCount = 0;
		const errors: string[] = [];
		for (const item of selected) {
			const res = await deactivateContract(item.id);
			if (res.status === 200 || res.status === 204) {
				successCount++;
			} else if (res.error?.message) {
				errors.push(res.error.message);
			}
		}

		setIsDeleting(false);
		setShowDeleteDialog(false);
		setPendingDeleteContracts([]);

		if (successCount === count) {
			setBanner({
				message: `${count} contract${count !== 1 ? 's' : ''} deleted.`,
				severity: 'success',
			});
		} else {
			const errorDetail = errors.length > 0 ? ` ${errors.join('; ')}` : '';
			setBanner({
				message: `Deleted ${successCount} of ${count} contracts.${errorDetail}`,
				severity: 'warning',
			});
		}
		refetch();
	}, [pendingDeleteContracts, deactivateContract, setBanner, refetch]);

	// Get work plan ID from activity
	const workPlanId = useMemo(() => {
		if (!activity) return null;
		return typeof activity.workPlan === 'number'
			? activity.workPlan
			: activity.workPlan?.id || null;
	}, [activity]);

	// Get work plan info from context
	const workPlan = useMemo(() => {
		if (!workPlanId) return null;
		return workPlans.find((e) => e.id === workPlanId) || null;
	}, [workPlans, workPlanId]);

	// Get contracts for this activity
	const activityContracts = useMemo(() => {
		if (!activity) return [];
		return getContractsByActivity(activity.id);
	}, [activity, getContractsByActivity]);

	// Get sub-activities for this activity
	const subActivities = useMemo(() => {
		if (!activity) return [];
		return activities.filter((a) => {
			const parentId = typeof a.parent === 'number' ? a.parent : null;
			return parentId === activity.id;
		});
	}, [activities, activity]);

	// Calculate sub-activity totals
	const subActivityTotals = useMemo(() => {
		const allocated = subActivities.reduce((sum, a) => sum + (a.amount || 0), 0);
		return {
			allocated,
			count: subActivities.length,
		};
	}, [subActivities]);

	// Get entity name
	const entityName = useMemo(() => {
		if (!activity) return 'Unknown';
		return typeof activity.entity === 'number'
			? `Entity #${activity.entity}`
			: activity.entity?.name || 'Unknown';
	}, [activity]);

	// Get populated property values for component, category, expenseType
	const componentName = useMemo(() => {
		if (!activity?.component) return null;
		return typeof activity.component === 'object' ? activity.component?.name : null;
	}, [activity]);

	const categoryName = useMemo(() => {
		if (!activity?.category) return null;
		return typeof activity.category === 'object' ? activity.category?.name : null;
	}, [activity]);

	const expenseTypeName = useMemo(() => {
		if (!activity?.expenseType) return null;
		return typeof activity.expenseType === 'object' ? activity.expenseType?.name : null;
	}, [activity]);

	// Not found state (only after loading is complete)
	if (!activityLoading && !activity) {
		return (
			<Box sx={{ px: 3, py: 4 }}>
				<Typography>Activity not found</Typography>
			</Box>
		);
	}

	// Get work plan currency for contract amounts (safe access for loading state)
	const workPlanCurrency = workPlan?.currency || 'USD';
	const projectLocalCurrency = getProjectLocalCurrency(project);
	// Local currency + canonical FX rate derived from the activity's own planned
	// amounts, used to restate reporting-currency contract figures into local
	// when the contract carries no local commitment of its own (e.g. drafts).
	const activityFx = resolveActivityFxRate(activity, project);
	const activityLocalCurrency = activity?.currencyLocal || projectLocalCurrency;
	const activityBudgetRate = activityFx?.rate ?? null;

	// Computed amounts (safe access for loading state)
	const plannedAmount = activity?.amount || 0;

	// Committed and spent both have to be expressed in the work plan's
	// reporting currency to sit under the reporting-currency `planned` label
	// without lying. A contract's `commitmentAmount` is by convention already
	// in the work plan currency, but a contract with NO amount in that
	// currency at all (e.g. an ETB-only contract under a USD work plan)
	// cannot be safely included here — its commitment scalar would otherwise
	// be summed under the wrong label. Spent uses the same gate so committed
	// and spent stay on the same basis (Pratheep / Khaled, 2026-06-21 call:
	// "that 100,000 was ETB and the 100 was USD" — the previous code summed
	// them naively, producing the $100,100 figure in the screenshot).
	const contractContributesToReportingTotal = (c: (typeof activityContracts)[number]): boolean =>
		getContractAmountForCurrency(c, workPlanCurrency) != null;
	const committedAmount = activityContracts.reduce(
		(sum, c) =>
			contractContributesToReportingTotal(c)
				? sum + (getContractCommitmentAmount(c, workPlan) ?? 0)
				: sum,
		0
	);
	// Spent = redeemed V2 vouchers across this activity's contracts. Each
	// contract's totalPaid is in that contract's own currency, so only sum
	// contracts that carry an amount in the work plan (reporting) currency —
	// same gate as committedAmount so the two figures stay on the same basis.
	// The local equivalent is restated on read from the activity budget rate.
	const spentAmount = activityContracts.reduce(
		(sum, c) =>
			contractContributesToReportingTotal(c) ? sum + getContractAmountPaidReporting(c) : sum,
		0
	);
	const unallocatedAmount = plannedAmount - subActivityTotals.allocated;

	// Build stats for HeaderDetailPage (only when activity is loaded)
	const activityStatusColors = activity ? getStatusColor(activity.status) : null;

	const headerStats = activity
		? [
				// Status
				{
					label: 'Status',
					value: (
						<Chip
							label={getStatusLabel(activity.status, t)}
							size="small"
							sx={{
								bgcolor: activityStatusColors?.bg,
								color: activityStatusColors?.text,
								fontSize: { xs: '1rem', sm: '1.125rem' },
								fontWeight: 600,
								height: 28,
								px: 1,
							}}
						/>
					),
				},
				...(activity.dateStart || activity.dateEnd
					? [
							{
								label: 'Timeline',
								value: `${
									activity.dateStart
										? new Date(activity.dateStart).toLocaleDateString('en-US', {
												timeZone: 'UTC',
												month: 'short',
												day: 'numeric',
												year: 'numeric',
											})
										: 'TBD'
								} – ${
									activity.dateEnd
										? new Date(activity.dateEnd).toLocaleDateString('en-US', {
												timeZone: 'UTC',
												month: 'short',
												day: 'numeric',
												year: 'numeric',
											})
										: 'TBD'
								}`,
							},
						]
					: []),
				...(workPlan
					? [
							{
								label: 'Work Plan',
								value: (
									<Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
										<IconWorkPlan
											sx={{ fontSize: 18, color: GRADIENTS.activity.icon, flexShrink: 0 }}
										/>
										<TruncatedText text={workPlan.name} component="span" sx={{ maxWidth: 200 }} />
									</Stack>
								),
								to: `/project/${project?.idWbg}/spend-plans/work-planning/work-plan/${workPlan.id}`,
							},
						]
					: []),
				{ label: 'Responsible Entity', value: entityName },
				...(componentName ? [{ label: 'Component', value: componentName }] : []),
				...(categoryName ? [{ label: 'Category', value: categoryName }] : []),
				...(expenseTypeName ? [{ label: 'Expense Type', value: expenseTypeName }] : []),
				// Custom fields
				...customFieldsToStats(
					(project?.customFieldDefinitions || []).find((d) => d.entityType === 'activity')
						?.definitions || [],
					activity.customData || {}
				),
			]
		: [];

	return (
		<Box>
			{/* Header using reusable component - always visible with loading state */}
			<HeaderDetailPage
				loading={activityLoading}
				loadingActions={1}
				showFinancialSummary={true}
				icon={IconActivity}
				breadcrumb={{
					label: 'All Project Activities',
					to: `/project/${project?.idWbg}/spend-plans/activities`,
				}}
				title={activity?.name || ''}
				variant="activity"
				onEdit={!activityLoading && activity ? handleEdit : undefined}
				stats={headerStats}
				financialSummary={
					activity
						? {
								data: {
									currency: workPlanCurrency,
									currencyLocal: activity.currencyLocal,
									currencyReporting: workPlanCurrency,
									fx: activityFx,
									planned: plannedAmount,
									committed: committedAmount,
									spent: spentAmount,
								},
								subActivityAllocation:
									subActivities.length > 0
										? {
												allocated: subActivityTotals.allocated,
												unallocated: unallocatedAmount,
											}
										: undefined,
							}
						: undefined
				}
			/>

			{/* Content with padding */}
			<Box
				sx={{
					pb: spacing.xl,
					px: { xs: spacing.sm, sm: spacing.md, md: spacing.lg },
					pt: spacing.md,
				}}>
				{/* Show skeleton for content while loading */}
				{activityLoading ? (
					<PageDetailSkeleton leftCards={2} rightCards={2} />
				) : activity ? (
					<>
						{/* Main Layout: Left (8/12) + Right (4/12) columns */}
						<Grid container spacing={{ xs: spacing.md, md: spacing.lg }}>
							{/* Left Column (8/12): Sub-Activities & Contracts */}
							<Grid item xs={12} lg={8}>
								<Stack spacing={spacing.lg}>
									{/* Sub-Activities */}
									<CardActivities
										activities={subActivities}
										allActivities={activities}
										emptyMessage="No sub-activities created yet"
										onAdd={handleAddSubActivity}
										onOpen={handleOpenSubActivity}
										subtitle={`${subActivities.length} sub-activit${
											subActivities.length === 1 ? 'y' : 'ies'
										}`}
										title="Sub-Activities"
										tooltipAdd="Add Sub-Activity"
										variant="subActivities"
										themeColor={GRADIENTS.activity.icon}
									/>

									{/* Contracts */}
									<CardDetail
										icon={IconContract}
										title="Contracts"
										subtitle={`${activityContracts.length} contract${activityContracts.length !== 1 ? 's' : ''}`}
										variant="contract"
										themeColor={GRADIENTS.contract.icon}
										onAdd={handleLinkContract}
										tooltipAdd="Link Contract"
										emptyMessage="No contracts linked to this activity"
										items={activityContracts}
										selectable
										getItemKey={(c) => c.id}
										selectionActions={[
											{
												label: 'Deactivate',
												icon: IconDelete,
												onClick: handleDeleteContracts,
												color: '#ef4444',
											},
										]}
										renderItem={(contract) => {
											const supplierName = getContractSupplierName(contract);
											const contractAmount = getContractCommitmentAmount(contract, workPlan); // Surface every distinct contract amount currency that isn't
											// already shown by the headline (work plan currency) or by the
											// local restatement caption — e.g. an ETB amount on a contract
											// otherwise priced in USD should still appear here.
											const otherCurrencyAmounts = getContractAmountsByCurrency(contract).filter(
												(a) =>
													a.currency !== workPlanCurrency &&
													a.currency !== activityLocalCurrency &&
													a.amount > 0
											); // Prefer the contract's own captured local commitment; fall
											// back to restating the reporting amount via the activity
											// budget rate so a local figure still shows for drafts and
											// contracts without a stored local commitment.
											const contractAmountLocal =
												contract.commitmentAmount != null && contract.commitmentAmountLocal != null
													? contract.commitmentAmountLocal
													: contractAmount != null && activityBudgetRate != null
														? contractAmount * activityBudgetRate
														: null;

											return (
												<Stack
													key={contract.id}
													direction="row"
													alignItems="center"
													onClick={() => handleOpenContractPage(contract.id)}
													sx={{
														cursor: 'pointer',
														p: spacing.sm,
														transition: 'all 0.15s ease',
														'&:hover': {
															bgcolor: alpha(GRADIENTS.contract.icon, 0.06),
														},
													}}>
													<Box
														sx={{
															width: { xs: 36, sm: 40 },
															height: { xs: 36, sm: 40 },
															borderRadius: '50%',
															bgcolor: alpha(GRADIENTS.contract.icon, 0.12),
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															flexShrink: 0,
															mr: 1.5,
														}}>
														<IconContract
															sx={{ fontSize: { xs: 18, sm: 20 }, color: GRADIENTS.contract.icon }}
														/>
													</Box>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Typography
															sx={{
																color: '#111827',
																fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
																fontWeight: 700,
															}}
															noWrap>
															{getContractNum(contract)}
														</Typography>
														<Typography
															sx={{
																color: '#374151',
																fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
															}}
															noWrap>
															{supplierName}
														</Typography>
													</Box>
													<Stack
														direction="row"
														spacing={1.5}
														alignItems="center"
														sx={{ flexShrink: 0 }}>
														<Box sx={{ textAlign: 'right' }}>
															<Typography
																sx={{
																	color: '#111827',
																	fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
																	fontWeight: 600,
																}}>
																{contractAmount != null
																	? formatCurrency({
																			currency: workPlanCurrency,
																			value: contractAmount,
																		})
																	: '—'}
															</Typography>
															{contractAmountLocal != null && activityLocalCurrency && (
																<Typography
																	sx={{
																		color: '#6b7280',
																		fontSize: { xs: '1.0625rem', sm: '1.15rem' },
																		fontWeight: 500,
																	}}>
																	{formatCurrency({
																		currency: activityLocalCurrency,
																		value: contractAmountLocal,
																	})}
																</Typography>
															)}
															{otherCurrencyAmounts.map((extra) => (
																<Typography
																	key={extra.currency}
																	sx={{
																		color: '#6b7280',
																		fontSize: { xs: '1.0625rem', sm: '1.15rem' },
																		fontWeight: 500,
																	}}>
																	{formatCurrency({
																		currency: extra.currency,
																		value: extra.amount,
																	})}
																</Typography>
															))}
														</Box>
														<Chip
															label={contract.status}
															size="small"
															color={contract.status === 'ACTIVE' ? 'success' : 'default'}
															sx={{
																fontWeight: 600,
																fontSize: '0.875rem',
															}}
														/>
													</Stack>
												</Stack>
											);
										}}
									/>
								</Stack>
							</Grid>

							{/* Right Column (4/12): Physical Progress & Documents */}
							<Grid item xs={12} lg={4}>
								<Stack spacing={spacing.lg}>
									<CardPhysicalProgress
										activity={activity}
										financialProgress={
											plannedAmount > 0 ? (committedAmount / plannedAmount) * 100 : 0
										}
										onEntryAdded={refetch}
									/>
									<CardAiDocuments
										context={{
											type: 'activity',
											id: activity.id,
											name: activity.name,
										}}
										projectId={project?.id}
									/>
								</Stack>
							</Grid>
						</Grid>
					</>
				) : null}
			</Box>

			{/* Deactivate Contracts Confirmation Dialog */}
			<DialogActions
				isOpen={showDeleteDialog}
				onClose={handleCancelDelete}
				maxWidth={440}
				title={{ children: 'Deactivate Contracts' }}
				subtitle={{
					children: `Deactivate ${pendingDeleteContracts.length} contract${pendingDeleteContracts.length !== 1 ? 's' : ''}? This cannot be undone.`,
				}}
				actions={[
					{
						children: 'Cancel',
						color: 'inherit',
						onClick: handleCancelDelete,
						variant: 'text',
					},
					{
						children: isDeleting ? 'Deactivating…' : 'Deactivate',
						disabled: isDeleting,
						loading: isDeleting,
						onClick: handleConfirmDelete,
						variant: 'contained',
						sx: { bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } },
					},
				]}
			/>
		</Box>
	);
};
